<template>
  <div>
  <section class="prof-page" :style="placeTheme">
    <div class="container-fluid">
      <div class="prof-page__inner">
        <div v-if="statePage === 'init' || statePage === 'loading'" class="prof-card prof-state">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Carregando</span>
          </div>
          <p class="h5 mt-3 mb-0 text-muted">Carregando informações</p>
        </div>

        <div v-else-if="statePage === 'error'" class="prof-card prof-state">
          <img src="@/assets/images/animation_error.gif" :class="isMobile() ? 'w-75' : 'w-25'" alt="Error">
          <p class="h4 text-muted mt-3">Opss... não foi possível carregar o profissional</p>
          <button type="button" onclick="window.location.reload()" class="btn btn-primary mt-2">Tentar novamente</button>
        </div>

        <div v-else-if="professional" class="prof-card">
          <div class="prof-cover"></div>

          <div class="prof-body">
            <div class="row g-4">
              <div class="col-lg-4">
                <div class="prof-identity">
                  <div class="prof-avatar-wrap">
                    <img
                      :src="professional.photo ? url(professional.photo) : require('@/assets/images/no-photo.png')"
                      class="prof-avatar"
                      :alt="professional.prof_name"
                    >
                  </div>

                  <h1 class="prof-name">{{ professional.prof_name }}</h1>
                  <p v-if="councilLabel" class="prof-council mb-3">{{ councilLabel }}</p>

                  <div class="prof-badges">
                    <span v-if="attendsOnline" class="prof-badge">
                      <i class="fas fa-video"></i>
                      Teleconsulta
                    </span>
                    <span v-if="attendsOnsite" class="prof-badge prof-badge--onsite">
                      <i class="fas fa-briefcase-medical"></i>
                      Presencial
                    </span>
                  </div>

                  <div class="prof-field">
                    <label class="form-label" for="specId">Especialidade</label>
                    <select
                      id="specId"
                      class="form-control"
                      v-model="specId"
                      @change="onChangeSpecialty"
                    >
                      <option v-for="specialty in specialties" :key="specialty.id_spec" :value="Number(specialty.id_spec)">
                        {{ specialty.spec_name }}
                      </option>
                    </select>
                  </div>

                  <button
                    type="button"
                    class="btn btn-outline-primary btn-sm w-100 d-lg-none mb-3"
                    data-bs-toggle="modal"
                    data-bs-target="#modal-professional-info"
                  >
                    <i class="fas fa-info-circle me-1"></i>
                    Ver informações
                  </button>

                  <ProfessionalInfo
                    class="d-none d-lg-block"
                    :professional="professional"
                    :council-label="councilLabel"
                  />
                </div>
              </div>

              <div class="col-lg-8">
                <div class="prof-schedule">
                  <div class="prof-schedule__head">
                    <div>
                      <h2 class="prof-schedule__title">Horários disponíveis</h2>
                      <p class="prof-schedule__hint mb-0">
                        Escolha um horário para {{ scheduleOnline ? 'teleconsulta' : 'atendimento presencial' }}
                      </p>
                    </div>

                    <div v-if="showAttendanceTabs" class="prof-tabs" role="tablist">
                      <button
                        type="button"
                        role="tab"
                        :aria-selected="scheduleOnline"
                        :class="{ active: scheduleOnline }"
                        @click="toggleScheduleOnline(true)"
                      >
                        <i class="fas fa-video"></i>
                        Teleconsulta
                      </button>
                      <button
                        type="button"
                        role="tab"
                        :aria-selected="!scheduleOnline"
                        :class="{ active: !scheduleOnline }"
                        @click="toggleScheduleOnline(false)"
                      >
                        <i class="fas fa-briefcase-medical"></i>
                        Presencial
                      </button>
                    </div>
                  </div>

                  <div class="table-responsive mt-3">
                    <table class="table table-sm table-borderless mb-0">
                      <tbody>
                        <tr>
                          <th v-if="!hideBtnPreviousDatesProf" scope="col" class="text-start prof-nav" @click="previousPageProf()">
                            <i class="fas fa-chevron-left"></i>
                          </th>
                          <th v-else></th>

                          <th
                            scope="col"
                            class="text-center"
                            v-for="availabilityDate in professional?.availability_dates"
                            :key="availabilityDate.available_date_patient"
                          >
                            <small>
                              {{ availabilityDate.name_available_date_patient }} <br>
                              <b>
                                {{ date(availabilityDate.available_date_patient).format('DD') }}/{{ availabilityDate.name_month_available_date_patient }}
                              </b>
                            </small>
                          </th>

                          <th v-if="hideBtnNextDatesProf"></th>
                          <th
                            v-else-if="professional?.availability_dates?.length > 0"
                            scope="col"
                            class="text-end prof-nav"
                            @click="nextPageProf()"
                          >
                            <i class="fas fa-chevron-right"></i>
                          </th>
                        </tr>

                        <tr v-if="!professional.availability_dates?.length">
                          <td colspan="12">
                            <div class="prof-empty">
                              <img
                                :src="require('@/assets/images/save_to_bookmarks.svg')"
                                :style="{width: isMobile() ? '25%' : '12%'}"
                                alt="Nenhum horário"
                                class="rounded img-fluid"
                              >
                              <p class="text-muted mb-3">
                                Nenhum horário disponível para {{ scheduleOnline ? 'teleconsulta' : 'atendimento presencial' }}.
                              </p>
                              <button
                                class="btn btn-primary"
                                data-bs-target="#modal-waiting-list"
                                data-bs-toggle="modal"
                              >
                                Solicitar encaixe
                              </button>
                            </div>
                          </td>
                        </tr>

                        <template v-else>
                          <tr
                            v-for="indexRow in (quantityRows = getQuantityRows())"
                            :key="indexRow"
                            class="text-center"
                            :class="quantityRows > quantityRowsAllowed && indexRow > quantityRowsAllowed && !expandedTimesProf ? 'd-none' : ''"
                          >
                            <th></th>
                            <td
                              v-for="availabilityDate in professional.availability_dates"
                              :key="availabilityDate.available_date_patient + '-' + indexRow"
                            >
                              <button
                                v-if="availabilityDate.availability_times[indexRow-1]"
                                type="button"
                                class="btn btn-sm btn-pills btn-outline-primary"
                                @click="selectAvailability(availabilityDate.availability_times[indexRow-1], professional)"
                              >
                                <b>{{ availabilityDate.availability_times[indexRow - 1].available_time_patient }}</b>
                              </button>
                            </td>
                            <th></th>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                  </div>

                  <div class="text-center" v-if="quantityRows > 4">
                    <button v-if="expandedTimesProf" type="button" class="btn btn-link" @click="toggleExpandedTimesProf()">
                      Mostrar menos
                      <i class="fas fa-angle-double-up"></i>
                    </button>
                    <button
                      v-else-if="professional.availability_dates?.length > 0"
                      type="button"
                      class="btn btn-link"
                      @click="toggleExpandedTimesProf()"
                    >
                      Mostrar todos
                      <i class="fas fa-angle-double-down"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="prof-card prof-state">
          <p class="mb-0">Nenhuma informação encontrada.</p>
        </div>
      </div>
    </div>
  </section>

  <WaitingListModal :professional="professional" :cityId="cityId" :specId="specId" :isOnline="scheduleOnline" />

  <div
    class="modal fade"
    id="modal-professional-info"
    tabindex="-1"
    aria-labelledby="modal-professional-info-title"
    aria-hidden="true"
    :style="placeTheme"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content rounded shadow border-0">
        <div class="modal-header border-bottom">
          <h5 id="modal-professional-info-title" class="modal-title">{{ professional?.prof_name }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <ProfessionalInfo v-if="professional" :professional="professional" :council-label="councilLabel" />
        </div>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import http from "../../helpers/http";
import date from "../../helpers/date";
import Device from "../../helpers/device";
import str from "../../helpers/str";
import { profileTo } from "./paths";
import WaitingListModal from "./WaitingListModal.vue";
import ProfessionalInfo from "./ProfessionalInfo.vue";

export default {
  name: "ProfessionalProfileIndex",
  components: {ProfessionalInfo, WaitingListModal},
  data() {
    return {
      statePage: 'init',
      professional: null,
      professionalid: null,
      cityId: null,
      placeId: null,
      specId: null,
      quantityRows: null,
      specialties: [],
      expandedTimesProf: false,
      hideBtnPreviousDatesProf: true,
      hideBtnNextDatesProf: false,
      currentStartDate: null,
      startEndDays: this.isMobile() ? 3 : 7,
      quantityRowsAllowed: this.isMobile() ? 4 : 8,
      scheduleOnline: false,
      placeColor: null,
      hadSpecialtyInUrl: false,
    }
  },
  mounted() {
    this.statePage = 'loading'
    const rawId = this.$route.params.professionalId;
    this.professionalid = rawId != null ? (parseInt(rawId, 10) || rawId) : null;
    this.cityId = this.$route.query.city;
    this.placeId = this.$route.query.place;
    this.loadPlaceColor();
    this.getSpecialties().then(() => {
      this.hadSpecialtyInUrl = this.$route.query.specialty != null && this.$route.query.specialty !== '';
      this.specId = this.resolveSpecId();
      this.syncSpecialtyQuery();
      return this.findProfessional();
    }).then(() => {
      this.statePage = 'loaded'
    }).catch(() => {
      this.statePage = 'error';
    });
  },
  computed: {
    specialty() {
      return this.specialties.find(spec => Number(spec.id_spec) === Number(this.specId));
    },
    releaseFor() {
      return this.professional?.default_release_time_for || 'B';
    },
    attendsOnline() {
      return ['O', 'B'].includes(this.releaseFor);
    },
    attendsOnsite() {
      return ['F', 'B'].includes(this.releaseFor);
    },
    showAttendanceTabs() {
      return this.attendsOnline && this.attendsOnsite;
    },
    placeTheme() {
      return {
        '--place-color': this.professional?.place_color || this.placeColor || this.$store.getters['professionalProfile/placeColor'],
      };
    },
    councilAcronym() {
      return (this.specialty?.kind_acronym || this.professional?.kind_acronym || '').trim();
    },
    councilNumber() {
      return String(this.specialty?.prof_council || this.professional?.prof_council || '').trim();
    },
    councilState() {
      return (this.specialty?.prof_council_state || this.professional?.prof_council_state || '').trim();
    },
    councilLabel() {
      if (!this.councilAcronym && !this.councilNumber) {
        return '';
      }

      const acronym = this.councilState && this.councilAcronym
        ? `${this.councilAcronym}/${this.councilState}`
        : this.councilAcronym;

      if (acronym && this.councilNumber) {
        return `${acronym} ${this.councilNumber}`;
      }

      return acronym || this.councilNumber;
    },
  },
  methods: {
    date: date,
    url(path) {
      return str.url(path)
    },
    isMobile() {
      return Device.isMobile();
    },
    resolveSpecId() {
      const ids = this.specialties.map(spec => Number(spec.id_spec));
      const queryId = Number(this.$route.query.specialty);
      if (this.$route.query.specialty != null && this.$route.query.specialty !== '' && ids.includes(queryId)) {
        return queryId;
      }

      return ids[0] ?? null;
    },
    syncSpecialtyQuery() {
      if (this.specId == null || Number(this.$route.query.specialty) === Number(this.specId)) {
        return;
      }

      this.$router.replace({
        query: {
          ...this.$route.query,
          specialty: this.specId,
        },
      });
    },
    onChangeSpecialty() {
      this.specId = Number(this.specId);
      this.syncSpecialtyQuery();
      this.loadAvailabilityProfessional();
    },
    loadPlaceColor() {
      if (!this.placeId) {
        return;
      }
      http.get(`/places/${this.placeId}/data`).then((response) => {
        this.placeColor = response.data.place_color;
        if (response.data.place_color) {
          this.$store.commit('professionalProfile/setPlaceColor', response.data.place_color);
        }
      });
    },
    applyAttendanceMode() {
      if (!this.attendsOnsite) {
        this.scheduleOnline = true;
        return;
      }
      if (!this.attendsOnline) {
        this.scheduleOnline = false;
        return;
      }
      if (this.professional.default_release_time_for === 'O') {
        this.scheduleOnline = true;
      } else if (this.professional.default_release_time_for === 'F') {
        this.scheduleOnline = false;
      }
    },
    toggleScheduleOnline(isOnline) {
      this.scheduleOnline = isOnline;
      this.loadAvailabilityProfessional();
    },
    findProfessional() {
      let params = {
        start_end_days: this.startEndDays,
        id_prof: this.professionalid,
        id_place: this.placeId,
        id_spec: this.specId,
        id_city: this.scheduleOnline ? null : this.cityId,
      };

      return http.get(`/schedule/professionals/${this.professionalid}`, {params: params}).then((response) => {
        this.professional = response.data;
        if (this.professional?.place_color) {
          this.$store.commit('professionalProfile/setPlaceColor', this.professional.place_color);
        }
        this.applyAttendanceMode();
        return this.fetchAvailability().then(() => this.ensureSpecialtyWithAvailability());
      });
    },
    fetchAvailability() {
      let params = {
        with_availability: true,
        start_end_days: this.startEndDays,
        id_prof: this.professionalid,
        id_place: this.placeId,
        id_city: this.scheduleOnline ? null : this.cityId,
        id_spec: this.specId,
      };

      return http.get(`/schedule/professionals/${this.professionalid}`, {params: params}).then((response) => {
        if (this.professional) {
          this.professional.availability_dates = response.data.availability_dates;
        }
        return response.data.availability_dates;
      });
    },
    loadAvailabilityProfessional() {
      this.hideBtnPreviousDatesProf = true;
      this.hideBtnNextDatesProf = false;
      this.currentStartDate = null;
      this.fetchAvailability();
    },
    async ensureSpecialtyWithAvailability() {
      if (this.hadSpecialtyInUrl || this.professional?.availability_dates?.length || this.specialties.length < 2) {
        return;
      }

      for (const spec of this.specialties) {
        const id = Number(spec.id_spec);
        if (id === Number(this.specId)) {
          continue;
        }

        const response = await http.get(`/schedule/professionals/${this.professionalid}`, {
          params: {
            with_availability: true,
            start_end_days: this.startEndDays,
            id_prof: this.professionalid,
            id_place: this.placeId,
            id_city: this.scheduleOnline ? null : this.cityId,
            id_spec: id,
          },
        });

        if (response.data?.availability_dates?.length) {
          this.specId = id;
          this.professional.availability_dates = response.data.availability_dates;
          this.syncSpecialtyQuery();
          return;
        }
      }
    },
    toggleExpandedTimesProf() {
      this.expandedTimesProf = !this.expandedTimesProf;
    },
    getQuantityRows() {
      let quantityRows = 0;
      this.professional.availability_dates?.forEach(function (availabilityDate) {
        let quantityTimes = availabilityDate.availability_times.length
        if (quantityTimes > quantityRows)
          quantityRows = quantityTimes;
      });

      return quantityRows;
    },
    findCity() {
      return http.get(`/cities/${this.cityId}`);
    },
    async selectAvailability(availabilityTime, professional) {
      if (!this.scheduleOnline && this.cityId) {
        let responseCity = await this.findCity();
        this.$store.commit('professionalProfile/selectCity', responseCity.data);
      } else {
        this.$store.commit('professionalProfile/selectCity', null);
      }

      let specialty = this.specialties.find(spec => Number(spec.id_spec) === Number(this.specId));
      this.$store.commit('professionalProfile/selectSpecialty', specialty);

      const selected = { ...professional };
      delete selected.availability_dates;
      this.$store.commit('professionalProfile/selectAvailability', {
        professional: selected,
        availabilityTime,
      });

      await this.$router.push(profileTo(this, 'paymentMethods', {
        professionalId: selected.id_prof,
        city: selected.id_city,
        place: selected.id_place,
        specialty: this.specId,
      }));
    },
    nextPageProf() {
      let professional = this.professional;
      let availabilityDates = professional.availability_dates;

      let startAvailability = availabilityDates[1];
      if (!startAvailability) {
        this.hideBtnNextDatesProf = true;
        return;
      }

      let startDate = startAvailability.available_date_patient;
      http.get('/schedule/availabilities/dates-times', {
        params: {
          id_place: professional.id_place,
          id_prof: professional.id_prof,
          id_spec: this.specId,
          id_city: this.scheduleOnline ? null : this.cityId,
          start_date: startDate,
          start_end_days: this.startEndDays,
        }
      }).then((response) => {
        let availability = response.data;
        this.currentStartDate = null;
        this.hideBtnPreviousDatesProf = false;
        this.hideBtnNextDatesProf = availability.length < this.startEndDays;
        this.professional.availability_dates = availability;
      });
    },
    previousPageProf() {
      let professional = this.professional;
      let availabilityDates = professional.availability_dates;
      let startAvailability = availabilityDates[0];

      if (this.currentStartDate) {
        this.currentStartDate = date(this.currentStartDate).add(-1, 'day').format('YYYY-MM-DD');
      } else {
        this.currentStartDate = date(startAvailability.available_date_patient).add(-1, 'day').format('YYYY-MM-DD');
      }

      http.get('/schedule/availabilities/dates-times', {
        params: {
          id_place: professional.id_place,
          id_prof: professional.id_prof,
          id_spec: this.specId,
          id_city: this.scheduleOnline ? null : this.cityId,
          start_date: this.currentStartDate,
          start_end_days: this.startEndDays,
        }
      }).then((response) => {
        let availability = response.data;
        let firstDate = availability[0]?.available_date_patient;
        this.hideBtnPreviousDatesProf = !firstDate || date(firstDate) <= date();
        this.hideBtnNextDatesProf = availability.length <= 0;
        this.professional.availability_dates = availability;
      });
    },
    getSpecialties() {
      const profId = parseInt(this.professionalid, 10) || this.professionalid;
      return http.get(`/professionals/${profId}/specialties`).then((specialties) => this.specialties = specialties.data);
    },
  }
}
</script>

<style scoped>
.prof-page {
  --place-color: #780f5a;
  padding: 110px 0 48px;
  background: linear-gradient(180deg, color-mix(in srgb, var(--place-color) 12%, #fff) 0%, #f4f6f8 42%, #f4f6f8 100%);
  min-height: 100vh;
}

.prof-page__inner {
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 16px;
}

.prof-card {
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 18px 48px color-mix(in srgb, var(--place-color) 16%, transparent);
  overflow: hidden;
}

.prof-cover {
  height: 132px;
  background:
    radial-gradient(circle at 18% 20%, rgba(255,255,255,0.22), transparent 36%),
    linear-gradient(
      135deg,
      color-mix(in srgb, var(--place-color) 78%, #000) 0%,
      var(--place-color) 48%,
      color-mix(in srgb, var(--place-color) 70%, #fff) 100%
    );
}

.prof-body {
  padding: 0 28px 32px;
}

.prof-identity {
  margin-top: -56px;
}

.prof-avatar-wrap {
  width: 118px;
  height: 118px;
  border-radius: 50%;
  padding: 4px;
  background: #fff;
  box-shadow: 0 10px 24px rgba(27, 16, 24, 0.12);
}

.prof-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.prof-name {
  font-size: 1.7rem;
  font-weight: 700;
  color: #2b1630;
  margin: 18px 0 4px;
  line-height: 1.2;
}

.prof-council {
  color: #6b5a66;
  font-weight: 600;
}

.prof-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
}

.prof-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: color-mix(in srgb, var(--place-color) 14%, #fff);
  color: var(--place-color);
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 0.82rem;
  font-weight: 600;
}

.prof-badge--onsite {
  background: color-mix(in srgb, var(--place-color) 8%, #f4f6f8);
  color: color-mix(in srgb, var(--place-color) 55%, #35516b);
}

.prof-field {
  margin-bottom: 20px;
}

.prof-schedule {
  padding: 24px 8px 8px;
}

.prof-schedule__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.prof-schedule__title {
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 4px;
  color: #2b1630;
}

.prof-schedule__hint {
  color: #7a6674;
  font-size: 0.92rem;
}

.prof-tabs {
  display: inline-flex;
  background: color-mix(in srgb, var(--place-color) 12%, #fff);
  border-radius: 999px;
  padding: 4px;
}

.prof-tabs button {
  border: 0;
  background: transparent;
  border-radius: 999px;
  padding: 8px 16px;
  color: var(--place-color);
  font-weight: 600;
  font-size: 0.9rem;
}

.prof-tabs button.active {
  background: var(--place-color);
  color: #fff;
  box-shadow: 0 6px 16px color-mix(in srgb, var(--place-color) 32%, transparent);
}

.prof-nav {
  cursor: pointer;
  color: var(--place-color);
}

.prof-page :deep(.btn-primary) {
  background-color: var(--place-color) !important;
  border-color: var(--place-color) !important;
}

.prof-page :deep(.btn-outline-primary) {
  color: var(--place-color) !important;
  border-color: var(--place-color) !important;
}

.prof-page :deep(.btn-outline-primary:hover),
.prof-page :deep(.btn-outline-primary:focus) {
  background-color: var(--place-color) !important;
  color: #fff !important;
}

.prof-page :deep(.btn-link) {
  color: var(--place-color);
}

.prof-page :deep(.bg-primary) {
  background-color: var(--place-color) !important;
}

.prof-empty {
  text-align: center;
  padding: 24px 8px 16px;
}

.prof-state {
  text-align: center;
  padding: 48px 24px;
}

@media (max-width: 991px) {
  .prof-cover {
    height: 88px;
  }

  .prof-identity {
    text-align: center;
    margin-top: -44px;
  }

  .prof-avatar-wrap {
    width: 88px;
    height: 88px;
    margin: 0 auto;
  }

  .prof-name {
    font-size: 1.35rem;
    margin-top: 12px;
  }

  .prof-badges {
    justify-content: center;
    margin-bottom: 12px;
  }

  .prof-schedule {
    padding: 0 0 8px;
  }

  .prof-schedule__title {
    font-size: 1rem;
  }

  .prof-tabs {
    width: 100%;
  }

  .prof-tabs button {
    flex: 1;
  }
}
</style>
